"use client";

import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { authApi, api } from '@/lib/api';
import { authUtils } from '@/lib/auth';
import type { 
  AuthState, 
  AuthContextType, 
  LoginCredentials, 
  User 
} from '@/types/auth';

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; accessToken: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_BUSINESS_UNIT'; payload: string }
  | { type: 'CLEAR_BUSINESS_UNIT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        isAuthenticated: true,
        isLoading: false,
      };
    
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        accessToken: null,
        isAuthenticated: false,
        businessUnit: null,
        isLoading: false,
      };
    
    case 'SET_BUSINESS_UNIT':
      return {
        ...state,
        businessUnit: action.payload,
      };
    
    case 'CLEAR_BUSINESS_UNIT':
      return {
        ...state,
        businessUnit: null,
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };
    
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, authUtils.getInitialAuthState());
  const router = useRouter();
  const pathname = usePathname();
  const initialPathnameRef = useRef(pathname);
  const debugEnabledRef = useRef(false);
  const stateRef = useRef(state);
  const pathnameRef = useRef(pathname);

  const authDebug = useCallback((event: string, meta?: Record<string, unknown>) => {
    if (!debugEnabledRef.current) return;
    const currentState = stateRef.current;
    const snapshot = {
      isAuthenticated: currentState.isAuthenticated,
      hasUser: !!currentState.user,
      hasAccessToken: !!currentState.accessToken,
      businessUnit: currentState.businessUnit,
      pathname: pathnameRef.current,
    };
    console.debug(`[AuthContext] ${event}`, { ...snapshot, ...meta });
  }, []);

  useEffect(() => {
    try {
      const debugFromStorage = localStorage.getItem('authDebug') === 'true';
      const debugFromEnv = process.env.NEXT_PUBLIC_AUTH_DEBUG === 'true';
      debugEnabledRef.current = debugFromStorage || debugFromEnv;
      authDebug('debug_mode_initialized', {
        debugFromStorage,
        debugFromEnv,
      });
    } catch {
      debugEnabledRef.current = process.env.NEXT_PUBLIC_AUTH_DEBUG === 'true';
    }
  }, [authDebug]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const isTokenValid = (token: string): boolean => {
      try {
        const parts = token.split('.');
        if (parts.length !== 3) return false;
        
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          return false;
        }
        
        return true;
      } catch (error) {
        console.error('[AuthContext] Error validating token:', error);
        return false;
      }
    };

    const getTokenExpiry = (token: string): number | null => {
      try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.exp) {
          return payload.exp * 1000;
        }
        return null;
      } catch {
        return null;
      }
    };

    const isTokenExpiringSoon = (token: string): boolean => {
      const expiry = getTokenExpiry(token);
      if (!expiry) return true;
      
      const now = Date.now();
      const timeUntilExpiry = expiry - now;
      const twoMinutes = 2 * 60 * 1000; 
      
      return timeUntilExpiry < twoMinutes;
    };

    const isNetworkError = (err: unknown): boolean => {
      if (!err || typeof err !== 'object') return false;
      const e = err as { code?: string; message?: string };
      return (
        e.code === 'ERR_NETWORK' ||
        e.code === 'ECONNABORTED' ||
        (typeof e.message === 'string' && (
          e.message.includes('Network Error') ||
          e.message.includes('timeout')
        ))
      );
    };

    const refreshTokenProactively = async (): Promise<boolean> => {
      authDebug('refresh_started');
      try {
        const refreshResponse = await api.post('/auth/refresh');
        
        if (refreshResponse.data?.accessToken) {
          const tokenExpiry = getTokenExpiry(refreshResponse.data.accessToken);
          if (tokenExpiry) {
            const expires = new Date(tokenExpiry);
            document.cookie = `accessToken=${refreshResponse.data.accessToken};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
          }
          let user = authUtils.getUser();
          if (!user) {
            try {
              user = await authApi.getCurrentUser() as User;
              authUtils.setUser(user);
              authDebug('refresh_recovered_user_from_api');
            } catch (userFetchError) {
              authDebug('refresh_user_fetch_failed', {
                error: userFetchError instanceof Error ? userFetchError.message : 'unknown',
              });
              user = null;
            }
          }

          if (user) {
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: { user, accessToken: refreshResponse.data.accessToken },
            });
          } else {
            authDebug('refresh_missing_user_after_token');
            return false;
          }
          authDebug('refresh_succeeded', {
            hasReturnedToken: !!refreshResponse.data?.accessToken,
          });
          return true;
        }
        authDebug('refresh_failed_no_token');
        return false;
      } catch (error: unknown) {
        if (isNetworkError(error)) {
          console.warn('[AuthContext] Network error during refresh');
          authDebug('refresh_failed_network');
          return false;
        }
        
        console.error('[AuthContext] Proactive refresh failed:', error);
        authDebug('refresh_failed_error', {
          error: error instanceof Error ? error.message : 'unknown',
        });
        return false;
      }
    };

    const initializeAuth = async () => {
      authDebug('init_started');
      const impToken = authUtils.getImpersonationToken();
      if (impToken) {
        authDebug('init_impersonation_detected');
        try {
          if (!isTokenValid(impToken)) {
            authDebug('init_impersonation_invalid_token');
            authUtils.clearImpersonationToken();
            if (typeof window !== 'undefined') window.location.href = '/signin';
            return;
          }
          const userData = await authApi.getCurrentUser() as User;
          const match = initialPathnameRef.current.match(/^\/dashboard\/([^/]+)/);
          const businessUnitFromPath = match ? match[1] : authUtils.getBusinessUnit();
          if (businessUnitFromPath) {
            authUtils.setBusinessUnit(businessUnitFromPath);
            dispatch({ type: 'SET_BUSINESS_UNIT', payload: businessUnitFromPath });
          }
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user: userData, accessToken: impToken },
          });
          authDebug('init_impersonation_login_success');
        } catch (err) {
          console.error('[AuthContext] Impersonation init failed:', err);
          authDebug('init_impersonation_failed', {
            error: err instanceof Error ? err.message : 'unknown',
          });
          authUtils.clearImpersonationToken();
          if (typeof window !== 'undefined') window.location.href = '/signin';
        }
        return;
      }

      const user = authUtils.getUser();
      const accessToken = authUtils.getAccessToken();
      const businessUnit = authUtils.getBusinessUnit();
      authDebug('init_storage_snapshot', {
        hasStoredUser: !!user,
        hasStoredToken: !!accessToken,
        storedBusinessUnit: businessUnit,
      });

      if (user && !accessToken) {
        authDebug('init_user_without_token_attempt_refresh');
        const refreshed = await refreshTokenProactively();
        if (!refreshed) {
          console.error('[AuthContext] Failed to refresh token');
          authDebug('init_user_without_token_logout');
          authUtils.clearAll();
          dispatch({ type: 'LOGOUT' });
        }
        return;
      }

      if (user && accessToken) {
        if (!isTokenValid(accessToken)) {
          authDebug('init_token_invalid_attempt_refresh');
          const refreshed = await refreshTokenProactively();
          if (!refreshed) {
            console.error('[AuthContext] Failed to refresh token');
            authDebug('init_invalid_token_logout');
            authUtils.clearAll();
            dispatch({ type: 'LOGOUT' });
          }
          return;
        }

        if (isTokenExpiringSoon(accessToken)) {
          refreshTokenProactively();
        }

        authUtils.setAccessToken(accessToken);
        authUtils.setUser(user);

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, accessToken },
        });
        authDebug('init_login_success');

        if (businessUnit) {
          authUtils.setBusinessUnit(businessUnit);
          dispatch({ type: 'SET_BUSINESS_UNIT', payload: businessUnit });
          authDebug('init_business_unit_applied', { businessUnit });
        }
      } else if (!user && accessToken) {
        authDebug('init_token_without_user_attempt_recover');
        if (!isTokenValid(accessToken)) {
          const refreshed = await refreshTokenProactively();
          if (!refreshed) {
            authDebug('init_token_without_user_invalid_logout');
            authUtils.clearAll();
            dispatch({ type: 'LOGOUT' });
          }
          return;
        }

        try {
          const recoveredUser = await authApi.getCurrentUser() as User;
          authUtils.setUser(recoveredUser);
          authUtils.setAccessToken(accessToken);
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user: recoveredUser, accessToken },
          });
          if (businessUnit) {
            authUtils.setBusinessUnit(businessUnit);
            dispatch({ type: 'SET_BUSINESS_UNIT', payload: businessUnit });
          }
          authDebug('init_token_without_user_recovered');
        } catch (recoverError) {
          authDebug('init_token_without_user_recover_failed', {
            error: recoverError instanceof Error ? recoverError.message : 'unknown',
          });
          const refreshed = await refreshTokenProactively();
          if (!refreshed) {
            authUtils.clearAll();
            dispatch({ type: 'LOGOUT' });
          }
        }
      } else {
        authDebug('init_no_session_found');
      }
    };

    initializeAuth().catch((error) => {
      console.error('[AuthContext] Error during initialization:', error);
    });
    const intervalId = setInterval(() => {
      if (authUtils.getImpersonationToken()) return;
      const accessToken = authUtils.getAccessToken();
      const user = authUtils.getUser();

      if (!accessToken && user) {
        refreshTokenProactively().catch(() => {});
      } else if (accessToken && isTokenExpiringSoon(accessToken)) {
        refreshTokenProactively().catch(() => {});
      }
    }, 60 * 1000); 

    return () => {
      clearInterval(intervalId);
    };
  }, [authDebug]);

  useEffect(() => {
    if (!state.isAuthenticated) return;
    const match = pathname.match(/^\/dashboard\/([^/]+)/);
    const businessUnitFromPath = match ? match[1] : null;
    if (!businessUnitFromPath || state.businessUnit === businessUnitFromPath) return;
    authUtils.setBusinessUnit(businessUnitFromPath);
    dispatch({ type: 'SET_BUSINESS_UNIT', payload: businessUnitFromPath });
    authDebug('business_unit_synced_from_path', { businessUnitFromPath });
  }, [pathname, state.isAuthenticated, state.businessUnit, authDebug]);

  useEffect(() => {
    if (state.isAuthenticated && pathname === '/signin') {
      const businessUnit = authUtils.getBusinessUnit();
      if (businessUnit) {
        router.push(`/dashboard/${businessUnit}`);
      } else {
        router.push('/choose-business-unit');
      }
    }
  }, [state.isAuthenticated, pathname, router]);

  const login = async (credentials: LoginCredentials) => {
    try {
      authDebug('login_started');
      dispatch({ type: 'LOGIN_START' });
      
      const response = await authApi.login(credentials);
      let cookieDays: number | undefined;
      if (response.refreshTokenExpiry) {
        cookieDays = Math.floor(response.refreshTokenExpiry / (24 * 60 * 60));
      }
      
      const getTokenExpiry = (token: string): number | null => {
        try {
          const parts = token.split('.');
          if (parts.length !== 3) return null;
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (payload.exp) return payload.exp * 1000;
          return null;
        } catch {
          return null;
        }
      };
      
      const tokenExpiry = getTokenExpiry(response.accessToken);
      if (tokenExpiry && cookieDays) {
        const expires = new Date(Date.now() + (response.refreshTokenExpiry! * 1000));
        document.cookie = `accessToken=${response.accessToken};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      } else if (tokenExpiry) {
        const expires = new Date(tokenExpiry);
        document.cookie = `accessToken=${response.accessToken};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      } else {
        authUtils.setAccessToken(response.accessToken, cookieDays);
      }
      
      authUtils.setUser(response.user);

      const businessUnit = authUtils.getBusinessUnit();
      if (businessUnit && cookieDays) {
        authUtils.setBusinessUnit(businessUnit, cookieDays);
      }
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.user,
          accessToken: response.accessToken,
        },
      });
      authDebug('login_succeeded', {
        userId: response.user?.id,
      });
    } catch (error) {
      console.error('Login error:', error);
      authDebug('login_failed', {
        error: error instanceof Error ? error.message : 'unknown',
      });
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = async () => {
    authDebug('logout_started', {
      isImpersonating: authUtils.isImpersonating(),
    });
    if (authUtils.isImpersonating()) {
      authUtils.clearImpersonationToken();
      authUtils.clearAll();
      dispatch({ type: 'LOGOUT' });
      authDebug('logout_impersonation_completed');
      if (typeof window !== 'undefined') window.location.href = '/signin';
      return;
    }
    try {
      await authApi.logout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      authUtils.clearAll();
      dispatch({ type: 'LOGOUT' });
      authDebug('logout_completed');
      if (typeof window !== 'undefined') {
        window.location.href = '/signin';
      }
    }
  };

  const setBusinessUnit = (businessUnit: string) => {
    authUtils.setBusinessUnit(businessUnit);
    dispatch({ type: 'SET_BUSINESS_UNIT', payload: businessUnit });
  };

  const clearBusinessUnit = () => {
    authUtils.removeBusinessUnit();
    dispatch({ type: 'CLEAR_BUSINESS_UNIT' });
  };

  const updateUser = useCallback((user: User) => {
    authUtils.setUser(user);
    dispatch({ type: 'SET_USER', payload: user });
  }, [dispatch]);

  const refetchCurrentUser = useCallback(async () => {
    try {
      const userInfo = await authApi.getCurrentUser();
      return userInfo;
      // updateUser(userInfo);
    } catch (error) {
      console.error('Failed to refresh user info:', error);
    }
  }, []);

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    setBusinessUnit,
    clearBusinessUnit,
    updateUser,
    refetchCurrentUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

